import type { FlowNode, SendMsgDingTalkRobotConfig } from '@lighthouse/shared'
import { getAllCanBeUpstreamNodes, getUpstreamNodesOptions, PAGE_LINK_OPTION, useAtomData } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type RichTextMsgProps = {
    markdown: SendMsgDingTalkRobotConfig['markdown']
}

const RichTextMsg: React.FC<RichTextMsgProps> = ({ markdown }) => {
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const extractDataText = useMemo(() => extractDataFn(markdown?.editorText), [markdown?.editorText])

    const nodesEditorText = useNodesData(extractDataText.nodeIds) as FlowNode[]

    const upstreamOptionsEditorText = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesEditorText || []),
                      allUpstreamNodes: nodesEditorText
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesEditorText]
    )

    if (!extractDataText.objects.length) {
        return null
    }
    return (
        <CM.CardItemContainer>
            {extractDataText.objects.length > 0 && (
                <>
                    <SC.TypeName>消息内容</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {extractDataText.objects.map(item => {
                                if (item.type === 'text') {
                                    return <SC.Text key={item.value}>{item.value}</SC.Text>
                                }
                                const text = textRichVariableName({
                                    userOption,
                                    upstreamOptions: upstreamOptionsEditorText,
                                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                                    data: item
                                })
                                return (
                                    <SC.Text key={text} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                )
                            })}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </>
            )}
        </CM.CardItemContainer>
    )
}

export default RichTextMsg
